import { print } from 'graphql';

import { useCurrentUserStore } from 'stores/current_user';

import { LOCAL_STORAGE_KEY, SESSION_KEY } from './utils';

export function useFastedApi() {
  const currentUserStore = useCurrentUserStore();

  const logout = () => {
    window.sessionStorage.removeItem(SESSION_KEY);
    if (window.localStorage.getItem(LOCAL_STORAGE_KEY) !== null) {
      window.localStorage.removeItem(LOCAL_STORAGE_KEY);
    }
    currentUserStore.teardown();
  };

  const restoreUser = () => {
    const sessionObject = window.localStorage.getItem(LOCAL_STORAGE_KEY) || window.sessionStorage.getItem(SESSION_KEY);
    if (sessionObject) {
      const parsedObject = JSON.parse(sessionObject);
      if (!parsedObject.user || !parsedObject.access_id || !parsedObject.secret) logout();
      else {
        currentUserStore.setUser(parsedObject.user);
      }
    } else {
      logout();
    }
  };

  const saveUser = (user, localStorage) => {
    if (user) {
      const sessionObject = {
        user: user,
        secret: user.secret,
        access_id: user.session.access_id,
      };
      if (localStorage || window.localStorage.getItem(LOCAL_STORAGE_KEY)) {
        window.localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(sessionObject));
      } else {
        window.sessionStorage.setItem(SESSION_KEY, JSON.stringify(sessionObject));
      }
      currentUserStore.setUser(user);
    } else {
      logout();
    }
  };

  const getDate = () => new Date().toUTCString();
  const sha256Fn = async (value) => {
    const encoder = new TextEncoder();
    const data = encoder.encode(value);
    return btoa(String.fromCharCode(...new Uint8Array(await crypto.subtle.digest('SHA-256', data))));
  };
  const canonicalString = async (body, date) => ['POST', 'application/json', await sha256Fn(body), '/graphql', date].join();
  const hmacFn = async (str) => {
    const sessionObject = JSON.parse(window.sessionStorage.getItem(SESSION_KEY) || window.localStorage.getItem(LOCAL_STORAGE_KEY));
    let enc = new TextEncoder('utf-8');
    let algorithm = { name: 'HMAC', hash: 'SHA-256' };

    let key = await crypto.subtle.importKey('raw', enc.encode(sessionObject.secret), algorithm, false, ['sign', 'verify']);
    let signature = await crypto.subtle.sign(algorithm.name, key, enc.encode(str));
    return btoa(String.fromCharCode(...new Uint8Array(signature)));
  };
  const getToken = async (body, date) => {
    const sessionObject = JSON.parse(window.sessionStorage.getItem(SESSION_KEY) || window.localStorage.getItem(LOCAL_STORAGE_KEY));
    return `APIAuth-HMAC-SHA256 ${sessionObject ? sessionObject.access_id : ''}:${await hmacFn(await canonicalString(body, date))}`;
  };

  const getHeaders = async (req) => {
    const date = getDate();
    const { operationName, variables } = req;
    const query = print(req.query);
    const body = JSON.stringify({
      operationName,
      variables,
      query,
    });
    return {
      'X-Date': date,
      'X-TIME_ZONE': (-1 * new Date().getTimezoneOffset()) / 60,
      Authorization: await getToken(body, date),
      'X-Authorization-Content-SHA256': await sha256Fn(body),
    };
  };

  const getErrorMessage = (msg) => {
    let errorMsg = '';
    if (msg?.errors && Array.isArray(msg.errors)) {
      for (const error of msg.errors) {
        if (error.message) {
          errorMsg += `${error.message}\n`;
        }
        if (error.extensions.validation_errors) {
          const validationMessages = error.extensions.validation_errors[0].messages[0];
          const attr = error.extensions.validation_errors[0].attribute;
          errorMsg += `${attr} - ${validationMessages}\n`;
        }
      }
      return errorMsg;
    }
    return msg?.error_message;
  };

  return {
    getDate,
    getErrorMessage,
    getHeaders,
    hmacFn,
    logout,
    restoreUser,
    saveUser,
    sha256Fn,
  };
}
